import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { Auth0Provider, AppState } from "@auth0/auth0-react";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { SettingsProvider } from "./hooks/useSettings";
import { auth0Config, validateAuth0Config } from "./auth0-config";

import Header from "./components/Header";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import HomePage from "./pages/HomePage";
import Dashboard from "./pages/Dashboard";
import ClozePractice from "./pages/ClozePractice";
import VerbParsingPractice from "./pages/VerbParsingPractice";
import VerbCollections from "./pages/VerbCollections";
import ProgressDashboard from "./pages/ProgressDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import AdminCollections from "./pages/AdminCollections";
import AdminClozes from "./pages/AdminClozes";
import SettingsPage from "./pages/SettingsPage";
import AboutPage from "./pages/AboutPage";
import CustomCollections from "./pages/CustomCollections";
import EditCustomCollection from "./pages/EditCustomCollection";

// Protected Route component
const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  requireAdmin?: boolean;
}> = ({ children, requireAdmin = false }) => {
  const { isAuthenticated, isAdmin, isLoading } = useAuth();

  // Don't redirect while still loading authentication state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/verbs" replace />;
  }

  return <>{children}</>;
};

// Main App component with conditional routing
const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading, user } = useAuth();

  console.log(
    "AppContent render - isAuthenticated:",
    isAuthenticated,
    "isLoading:",
    isLoading,
    "user:",
    user
  );

  // Show loading spinner while Auth0 is initializing
  if (isLoading) {
    return (
      <div className="min-h-screen bg-zinc-50 dark:bg-zinc-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto mb-4"></div>
          <p className="text-zinc-600 dark:text-zinc-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-900">
      <Header />
      <main className="container mx-auto px-4 py-4 md:pb-20">
        <Routes>
          {/* Public routes - when not authenticated */}
          <Route
            path="/login"
            element={
              isLoading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
                </div>
              ) : isAuthenticated ? (
                <Navigate to="/home" replace />
              ) : (
                <LoginPage />
              )
            }
          />
          <Route
            path="/register"
            element={
              isLoading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
                </div>
              ) : isAuthenticated ? (
                <Navigate to="/home" replace />
              ) : (
                <RegisterPage />
              )
            }
          />

          {/* Protected user routes */}
          <Route
            path="/home"
            element={
              <ProtectedRoute>
                <HomePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/practice"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/practice/:collectionId"
            element={
              <ProtectedRoute>
                <ClozePractice />
              </ProtectedRoute>
            }
          />
          <Route
            path="/custom-practice/:collectionId"
            element={
              <ProtectedRoute>
                <ClozePractice />
              </ProtectedRoute>
            }
          />
          <Route path="/verbs" element={<Navigate to="/practice" replace />} />
          <Route
            path="/verbs/:collectionId"
            element={
              <ProtectedRoute>
                <VerbParsingPractice />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/progress"
            element={
              <ProtectedRoute>
                <ProgressDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/about"
            element={
              <ProtectedRoute>
                <AboutPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/decks"
            element={
              <ProtectedRoute>
                <CustomCollections />
              </ProtectedRoute>
            }
          />
          <Route
            path="/decks/:collectionId/edit"
            element={
              <ProtectedRoute>
                <EditCustomCollection />
              </ProtectedRoute>
            }
          />

          {/* Protected admin routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute requireAdmin>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/collections"
            element={
              <ProtectedRoute requireAdmin>
                <AdminCollections />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/collections/:collectionId/clozes"
            element={
              <ProtectedRoute requireAdmin>
                <AdminClozes />
              </ProtectedRoute>
            }
          />

          {/* Default redirect */}
          <Route
            path="/"
            element={
              isLoading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
                </div>
              ) : isAuthenticated ? (
                <Navigate to="/home" replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="*"
            element={
              isLoading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600"></div>
                </div>
              ) : isAuthenticated ? (
                <Navigate to="/home" replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
      </main>

      {/* Footer - Relative on mobile, fixed at bottom on desktop */}
      <footer className="py-4 bg-white dark:bg-zinc-900 border-t border-zinc-200 dark:border-zinc-700 md:fixed md:bottom-0 md:left-0 md:right-0">
        <div className="container mx-auto px-4 text-center">
          <div className="flex flex-col md:flex-row items-center justify-center gap-2 md:gap-3 text-sm">
            <span className="text-zinc-500 dark:text-zinc-400">
              Ironroot Labs LLC, 2025
            </span>
            <span className="hidden md:inline text-zinc-300 dark:text-zinc-600">
              •
            </span>
            <a
              href="https://www.buymeacoffee.com/ironrootlabs"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="https://cdn.buymeacoffee.com/buttons/v2/default-yellow.png"
                alt="Buy Me A Coffee"
                style={{ height: "40px", width: "145px" }}
              />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Configuration Error Component
const ConfigError: React.FC = () => {
  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-900 flex items-center justify-center">
      <div className="max-w-md mx-auto bg-white dark:bg-zinc-800 rounded-lg shadow-md border border-zinc-200 dark:border-zinc-700 p-8">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20 mb-4">
            <svg
              className="h-6 w-6 text-red-600 dark:text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100 mb-4">
            Authentication Not Configured
          </h2>
          <p className="text-zinc-600 dark:text-zinc-400 mb-6">
            Auth0 authentication needs to be set up before you can use this
            application.
          </p>
          <div className="bg-zinc-50 dark:bg-zinc-700 rounded-md p-4 text-left">
            <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-2">
              To fix this:
            </h3>
            <ol className="text-sm text-zinc-600 dark:text-zinc-400 space-y-1">
              <li>
                1. Create a{" "}
                <code className="bg-zinc-200 dark:bg-zinc-600 px-1 rounded">
                  .env
                </code>{" "}
                file in the frontend directory
              </li>
              <li>2. Add your Auth0 credentials:</li>
            </ol>
            <pre className="mt-2 text-xs bg-zinc-200 dark:bg-zinc-600 p-2 rounded overflow-x-auto">
              {`REACT_APP_AUTH0_DOMAIN=your-actual-domain.auth0.com
REACT_APP_AUTH0_CLIENT_ID=your-actual-client-id
REACT_APP_AUTH0_AUDIENCE=https://api.greek-cloze.com`}
            </pre>
            <p className="text-xs text-zinc-500 dark:text-zinc-500 mt-2">
              Get these values from your Auth0 Dashboard under Applications
            </p>
          </div>
        </div>
      </div>

      {/* Footer - Relative on mobile, fixed at bottom on desktop */}
      <footer className="py-4 bg-white dark:bg-zinc-900 border-t border-zinc-200 dark:border-zinc-700 md:fixed md:bottom-0 md:left-0 md:right-0">
        <div className="container mx-auto px-4 text-center">
          <div className="flex flex-col md:flex-row items-center justify-center gap-2 md:gap-3 text-sm">
            <span className="text-zinc-500 dark:text-zinc-400">
              Ironroot Labs LLC, 2025
            </span>
            <span className="hidden md:inline text-zinc-300 dark:text-zinc-600">
              •
            </span>
            <a
              href="https://www.buymeacoffee.com/ironrootlabs"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="https://cdn.buymeacoffee.com/buttons/v2/default-yellow.png"
                alt="Buy Me A Coffee"
                style={{ height: "40px", width: "145px" }}
              />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Auth0 Provider with Router integration
const Auth0ProviderWithNavigate: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();

  const onRedirectCallback = (appState?: AppState) => {
    navigate(appState?.returnTo || window.location.pathname);
  };

  return (
    <Auth0Provider
      domain={auth0Config.domain}
      clientId={auth0Config.clientId}
      authorizationParams={{
        redirect_uri: auth0Config.redirectUri,
        audience: auth0Config.audience,
        scope: auth0Config.scope,
      }}
      onRedirectCallback={onRedirectCallback}
      useRefreshTokens={true}
      cacheLocation="localstorage"
    >
      {children}
    </Auth0Provider>
  );
};

// App content that requires routing
const AppWithRouter: React.FC = () => {
  return (
    <Router>
      <Auth0ProviderWithNavigate>
        <SettingsProvider>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </SettingsProvider>
      </Auth0ProviderWithNavigate>
    </Router>
  );
};

// Main App component with conditional rendering
const App: React.FC = () => {
  // Check if Auth0 is properly configured
  const isConfigured = validateAuth0Config();

  if (!isConfigured) {
    return <ConfigError />;
  }

  return <AppWithRouter />;
};

export default App;
